import * as SentryVue from '@sentry/vue';
import type {ErrorEvent, EventHint} from '@sentry/core';
import {setTag} from '@sentry/core';
import {environment} from '@/config/environment';
import type {App, Plugin} from 'vue';
import {useRouter} from 'vue-router';
import {useToast} from '@/shared/components/toast/useToast';

export const sentryPlugin = {
    install(app) {
        addErrorHandling(app);
        app.runWithContext(() => {
            const router = useRouter();
            const options = getInitOptions(app, router);

            if (__IS_HYBRID_APP__) {
                import('@sentry/capacitor').then(async SentryCapacitor => {
                    //@ts-ignore
                    SentryCapacitor.init(options, SentryVue.init);

                    const {CapacitorUpdater} = await import('@capgo/capacitor-updater');
                    const {bundle} = await CapacitorUpdater.current();

                    // Set tag for native errors
                    SentryCapacitor.setTag('capgo.bundle_id', bundle.version);

                    // Set tag for JS errors
                    setTag('capgo.bundle_id', bundle.version);
                });
            } else {
                SentryVue.init(options);
            }
        });

        window.addEventListener('vite:preloadError', event => {
            event.preventDefault();
            if (!sessionStorage.getItem('preload-retry')) {
                sessionStorage.setItem('preload-retry', '1');
                window.location.reload();
            }
        });
    },
} satisfies Plugin;

type SentryVueInitOptions = NonNullable<Parameters<typeof SentryVue.init>[0]>;

const sentryExceptions = [
    /failed to fetch/i,
    /load failed/i,
    /networkerror when attempting to fetch resource/i,
    /\(reading '_leaflet_pos'\)/i,
    /\(evaluating 'h\._leaflet_pos'\)/i,
    /error loading dynamically imported module/,
    /Importing a module script failed/,
];

const stackTraceSentryExceptions = [/_getMapPanePos/];

function getInitOptions(
    app: SentryVueInitOptions['app'],
    router: ReturnType<typeof useRouter>
): SentryVueInitOptions {
    return {
        app,
        dsn: environment.VITE_SENTRY_DSN_JS,
        environment: environment.VITE_SENTRY_ENVIRONMENT,
        integrations: [
            SentryVue.browserTracingIntegration({router}),
            SentryVue.extraErrorDataIntegration({depth: 3}),
            SentryVue.vueIntegration({
                tracingOptions: {
                    trackComponents: true,
                    hooks: ['create', 'mount', 'activate', 'destroy'],
                },
            }),
            SentryVue.replayIntegration(),
        ],
        replaysSessionSampleRate: 0.01,
        replaysOnErrorSampleRate: 0.5,
        tracesSampleRate: environment.VITE_SENTRY_TRACING_SAMPLE_RATE,
        beforeSend(event: ErrorEvent, hint: EventHint) {
            const error = hint?.originalException;

            if (!error) {
                return event;
            }

            if ([400, 401, 403, 404, 500].includes((error as {status?: number}).status!)) {
                return null;
            }

            const message = typeof error === 'string' ? error : (error as Error).message;

            if (typeof message !== 'string') {
                event.fingerprint = [JSON.stringify(event), JSON.stringify(hint)];
                return event;
            }

            if (message.startsWith('Unable to preload CSS')) {
                event.fingerprint = ['unable-to-preload-css'];
                return event;
            }

            if (message.startsWith('Failed to fetch dynamically imported module')) {
                event.fingerprint = ['failed-to-fetch-dynamically-imported-module'];
                return event;
            }

            if (sentryExceptions.some((regex: RegExp) => regex.test(message))) {
                return null;
            }

            function getStackTrace() {
                try {
                    return event.exception?.values
                        ?.map(exception =>
                            exception.stacktrace?.frames?.map(frame => frame.function)?.join('')
                        )
                        ?.filter(Boolean)
                        ?.join('');
                } catch {
                    return null;
                }
            }

            const stackTrace = getStackTrace();

            if (stackTrace && stackTraceSentryExceptions.some(regex => regex.test(stackTrace))) {
                return null;
            }

            return event;
        },
    };
}

function addErrorHandling(app: App) {
    // Hide console logs in prod
    if (!import.meta.env.DEV) {
        app.config.errorHandler = _ => {};
        return;
    }

    // Use default error handler -> throw errors
    if (environment.VITE_DEV_ERROR_HANDLING === 'throw') {
        return;
    }

    app.config.errorHandler = error => {
        // eslint-disable-next-line no-console
        console.error(error);
        if (environment.VITE_DEV_ERROR_HANDLING === 'alert') {
            useToast().error('A development error occurred.', {detail: error});
        }
    };
}
